/**
 * @file 知识库引用选择器
 * - button 模式：使用 NewRegionSelect 面板
 * - @ 模式：使用 Portal 下拉组件（参考 SkillMention）
 */
import React, {
  FC,
  memo,
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import ReactDOM, { unstable_batchedUpdates } from 'react-dom';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { useControllableValue, useHover, useMemoizedFn } from 'ahooks';
import { StatusTip } from 'tea-component';
import NewRegionSelect from '@home/components/NewRegionSelect';
import type { RegionSelectValueType } from '@home/components/NewRegionSelect/type';
import InputTriggerHoverTip from '../components/InputTriggerHoverTip';
import { useDismissOutside } from '../hooks/useDismissOutside';
import {
  getKnowledgeTagKey,
  type ISelectedKnowledgeTagData,
} from './components/SelectedKnowledgeTag';
import { useInteractiveKbDocs } from './hooks/useKbDocs';
import {
  buildAtDropdownGroups,
  isWholeKbTag,
  resolveTenantId,
} from './utils/knowledgeBaseUtils';
import {
  buildKnowledgeOptions,
  buildSingleKbClearRegionValue,
  buildSingleKbWholeRegionValue,
  clearWholeKbRegionsForKbList,
  expandRegionModeDisplayValue,
  mergeWholeKbRegionsForKbList,
  normalizeRegionModePanelValue,
} from './utils/kbSelectionUtils';
import { useKbButtonPanel } from './hooks/useKbButtonPanel';
import { useKbButtonPanelList } from './hooks/useKbButtonPanelList';
import { useKbAtDropdownList } from './hooks/useKbAtDropdownList';
import { useKbAtScrollDocLoader } from './hooks/useKbAtScrollDocLoader';
import { useKbPanelChrome } from './hooks/useKbPanelChrome';
import { useMentionDropdownPosition } from '../mentionShared/useMentionDropdownPosition';
import { buildKnowledgeInlineLabel } from '../utils/inlineMentionLabel';
import {
  PREFIX,
  KB_POPOVER_RIGHT_LIST_WIDTH,
  KB_POPOVER_RIGHT_LIST_HEIGHT,
  KB_HOVER_AUTO_CLOSE_DELAY_MS,
  type TAtFlatItem,
  type IKnowledgeBaseMentionProps,
} from './types';
import KbButtonTrigger from './components/KbButtonTrigger';
import KbPanelSearchInputPortal from './components/KbPanelSearchInputPortal';
import MentionDropdownSkeleton from '../mentionShared/MentionDropdownSkeleton';
import MentionDropdownSectionTitle from '../mentionShared/MentionDropdownSectionTitle';
import HighlightText from '@/pages/aigc/components/HighlightText';
import { getChannelIcon, KB_ICON } from './utils/channelIconMap';
import BasicImage from '@/pages/home/components/BasicImage';
import './index.scss';

/**
 * 左列当前高亮库变化时按需拉文档；搜索 keyword 在业务层监听面板搜索框同步
 */
const KbActiveRegionDocsLoader: React.FC<{
  kbId?: string;
  loadKbDocs: (ids: string[]) => void;
  onActiveKbId?: (kbId: string) => void;
}> = ({ kbId, loadKbDocs, onActiveKbId }) => {
  useEffect(() => {
    if (!kbId) {
      return;
    }
    onActiveKbId?.(kbId);
    loadKbDocs([kbId]);
  }, [kbId, loadKbDocs, onActiveKbId]);

  return null;
};

const KnowledgeBaseMention: FC<IKnowledgeBaseMentionProps> = ({
  selectedItems = [],
  onSelectedChange,
  className,
  open,
  onOpenChange,
  onOpenAsButton,
  mentionQuery = '',
  openReason = 'button',
  mentionAnchorRef,
  inputBoxContainerRef,
  isDropDown = false,
  from,
  onAtModeSelect,
  onLabelReplace: _onLabelReplace,
  enableHoverOpen = true,
  onBeforePanelOpen,
  onInsertAtCursor: _onInsertAtCursor,
  onPanelSync,
  onPanelCommit,
}) => {
  const { formatMessage, locale } = useIntl();
  const kbDocUnit = formatMessage({ id: 'dba_kb_inline_doc_unit' });
  const isCopilot = from === 'copilot';
  const enableHover = enableHoverOpen && !isCopilot;

  const [opened, setOpened] = useControllableValue<boolean>(
    { value: open, onChange: onOpenChange },
    { defaultValue: false, valuePropName: 'value', trigger: 'onChange' },
  );

  const triggerRef = useRef<HTMLSpanElement>(null);
  const isTriggerHovering = useHover(triggerRef);
  const hoveredRef = useRef(false);

  // ─── @ 模式下拉框 Portal 相关 ───
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [listEl, setListEl] = useState<HTMLDivElement | null>(null);
  /** 稳定 ref，避免内联 ref 每渲染 setState 导致 null↔el 抖动、@ 后无法输入 */
  const setListRef = useCallback((el: HTMLDivElement | null) => {
    listRef.current = el;
    setListEl((prev) => (prev === el ? prev : el));
  }, []);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const atModeOpenedRef = useRef(false);
  const prevAtSearchKeyRef = useRef('');

  /** 是否处于 @ 输入触发模式 */
  const isAtMode = openReason === 'at' && opened;
  /** button 模式：含从 @ 输入态 hover 切回后的面板 */
  const isButtonMode = openReason === 'button' && opened;

  /** @ 模式统一关闭：走 host 状态机，清 query / index / reason */
  const closeAtDropdown = useMemoizedFn(() => {
    onOpenChange?.(false);
  });
  /** @ 模式用 mentionQuery 过滤；button 模式走 NewRegionSelect 内置搜索 */
  const filterKeyword = isAtMode ? mentionQuery : '';

  const buttonPanelList = useKbButtonPanelList(opened, isButtonMode);
  const atDropdownList = useKbAtDropdownList(isAtMode, mentionQuery);

  const {
    panelKbList,
    kbPanelLoading,
    kbPanelError,
    panelSearchInput,
    handlePanelSearchChange,
    debouncedSearchKeyword,
  } = buttonPanelList;

  const {
    atKbList,
    atListLoading,
    atListError,
    debouncedAtQuery,
  } = atDropdownList;

  /** @ 模式用当前下拉列表建 kbMap，搜索结果才能 loadKbDocs */
  const fullKbList = isAtMode
    ? (atDropdownList.atKbList.length
      ? atDropdownList.atKbList
      : atDropdownList.fullKbList)
    : buttonPanelList.fullKbList;
  const kbLoading = isAtMode ? atListLoading : buttonPanelList.kbLoading;
  const kbError = isAtMode ? atListError : buttonPanelList.kbError;

  /** button 模式不做 options 二次过滤（搜索走服务端） */
  const optionsBuildKeyword = '';

  const [activePanelKbId, setActivePanelKbId] = useState('');

  const {
    data: kbList,
    docsByKbId,
    loadKbDocs,
    kbDocsLoadingIds,
  } = useInteractiveKbDocs(fullKbList, activePanelKbId);

  const kbDocsLoadingKey = kbDocsLoadingIds.join(',');
  const docsByKbIdKey = Object.keys(docsByKbId).sort().join(',');

  const knowledgeOptions = useMemo(
    () => buildKnowledgeOptions(
      panelKbList,
      optionsBuildKeyword,
      docsByKbId,
      panelSearchInput.trim(),
    ),
    [
      panelKbList,
      optionsBuildKeyword,
      docsByKbId,
      docsByKbIdKey,
      panelSearchInput,
    ],
  );

  const renderKbRightContent = useCallback(
    (selectedRegion: { value?: string; }) => {
      const kbId = String(selectedRegion?.value || '');
      if (!kbId) {
        return null;
      }
      if (kbDocsLoadingIds.includes(kbId)) {
        return (
          <div className={`${PREFIX}__right-panel-loading`}>
            <StatusTip status="loading" />
          </div>
        );
      }
      return null;
    },
    [kbDocsLoadingIds, kbDocsLoadingKey],
  );

  const {
    localSelectValue,
    localSelectValueRef,
    defaultSelectedRegion,
    regionMountKey,
    interactedRef,
    handleLocalSelectChange,
    handleOpenChange,
    handleRegionSelectClose: rawHandleRegionSelectClose,
    closePanel,
  } = useKbButtonPanel({
    opened,
    setOpened,
    openReason,
    isAtMode,
    selectedItems,
    kbList,
    docsByKbId,
    kbLoading,
    panelKbList,
    loadKbDocs,
    onSelectedChange,
    onPanelSync,
    onPanelCommit,
    onBeforePanelOpen,
    onOpenAsButton,
    hoveredRef,
  });

  const { overlayEl } = useKbPanelChrome({
    opened,
    isAtMode,
    isButtonMode,
    regionMountKey,
    panelKbList,
    kbPanelLoading,
    debouncedSearchKeyword,
    panelSearchInput,
    handlePanelSearchChange,
    activePanelKbId,
    setActivePanelKbId,
    loadKbDocs,
    interactedRef,
  });

  const isOverlayHovering = useHover(overlayEl);

  const handleActivePanelKbId = useMemoizedFn((kbId: string) => {
    setActivePanelKbId(kbId);
  });

  const regionSelectDisplayValue = useMemo(
    () => expandRegionModeDisplayValue(localSelectValue, knowledgeOptions),
    [localSelectValue, knowledgeOptions],
  );

  const handleRegionSelectDisplayChange = useMemoizedFn(
    (next: RegionSelectValueType) => {
      handleLocalSelectChange(
        normalizeRegionModePanelValue(
          localSelectValueRef.current,
          next,
          knowledgeOptions,
        ),
      );
    },
  );

  const handleRegionSelectClose = useMemoizedFn((result: RegionSelectValueType) => {
    rawHandleRegionSelectClose(
      normalizeRegionModePanelValue(
        localSelectValueRef.current,
        result,
        knowledgeOptions,
      ),
    );
  });

  const handleKbMarketSelectAll = useMemoizedFn(() => {
    if (!activePanelKbId) {
      return;
    }
    handleLocalSelectChange(
      buildSingleKbWholeRegionValue(localSelectValueRef.current, activePanelKbId),
    );
  });

  const handleKbMarketClearAll = useMemoizedFn(() => {
    if (!activePanelKbId) {
      return;
    }
    handleLocalSelectChange(
      buildSingleKbClearRegionValue(localSelectValueRef.current, activePanelKbId),
    );
  });

  const handleKbRegionSelectAll = useMemoizedFn(() => {
    if (!panelKbList.length) {
      return;
    }
    handleLocalSelectChange(
      mergeWholeKbRegionsForKbList(localSelectValueRef.current, panelKbList),
    );
  });

  const handleKbRegionClearAll = useMemoizedFn(() => {
    if (!panelKbList.length) {
      return;
    }
    handleLocalSelectChange(
      clearWholeKbRegionsForKbList(localSelectValueRef.current, panelKbList),
    );
  });

  useEffect(() => {
    hoveredRef.current = isTriggerHovering;
  }, [isTriggerHovering]);

  const handleTriggerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    onBeforePanelOpen?.();
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    if (enableHover) return;
    e.stopPropagation();
    e.preventDefault();
    if (!opened) {
      onBeforePanelOpen?.();
      onOpenAsButton?.();
    }
  };

  /** hover 打开：含 @ 输入态，悬停按钮时切回 NewRegionSelect 面板 */
  const handleTriggerMouseEnter = () => {
    if (!enableHover) return;
    hoveredRef.current = true;
    if (isAtMode || openReason !== 'button') {
      onOpenAsButton?.();
      return;
    }
    if (!opened) {
      onBeforePanelOpen?.();
      onOpenAsButton?.();
    }
  };

  // 离开 trigger + 浮层后延迟关闭（对齐 Skill：仅「未点过面板」时 hover 可关）
  useEffect(() => {
    if (!isButtonMode || !enableHover) return;
    if (interactedRef.current) return;
    if (isTriggerHovering || isOverlayHovering) return;
    const timer = window.setTimeout(closePanel, KB_HOVER_AUTO_CLOSE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [
    isButtonMode,
    enableHover,
    isTriggerHovering,
    isOverlayHovering,
    closePanel,
  ]);

  // 已选中的 id 集合
  const selectedIds = useMemo(
    () => new Set(selectedItems.map((item) => getKnowledgeTagKey(item))),
    [selectedItems],
  );

  /** 整库 @库名 已选中的 kbId（tag key 为 `${kbId}_all`，不能与单文档 key 混用） */
  const wholeSelectedKbIds = useMemo(
    () => new Set(
      selectedItems
        .filter(isWholeKbTag)
        .map((item) => String(item.kbId)),
    ),
    [selectedItems],
  );

  // ─── @ 模式：服务端筛库 + 合并已加载文档，只剔已选 ───
  const atFilteredData = useMemo(() => {
    if (!isAtMode) {
      return [];
    }
    return buildAtDropdownGroups(
      atKbList,
      kbList,
      wholeSelectedKbIds,
      selectedIds,
    );
  }, [isAtMode, atKbList, kbList, wholeSelectedKbIds, selectedIds]);

  /** 与下拉实际渲染的分组对齐，搜索换列表后才能重扫可见项 */
  const atDocsObserveKey = useMemo(
    () => `${debouncedAtQuery}|${atFilteredData.map(({ kb }) => String(kb.kbId)).join(',')}`,
    [debouncedAtQuery, atFilteredData],
  );

  // @ 模式下扁平化所有可选项（分组头 + 文档，均参与键盘导航）
  const atFlatItems = useMemo(() => {
    const items: TAtFlatItem[] = [];
    atFilteredData.forEach(({ kb, docs }) => {
      items.push({ type: 'kb', kb, docs });
      docs.forEach((doc) => {
        items.push({ type: 'doc', kb, doc });
      });
    });
    return items;
  }, [atFilteredData]);

  // @ 模式打开时高亮第一项；文档加载导致列表变长时不重置高亮
  useEffect(() => {
    if (!isAtMode) {
      atModeOpenedRef.current = false;
      prevAtSearchKeyRef.current = '';
      setHighlightIndex(-1);
      return;
    }
    if (atListLoading && atFlatItems.length === 0) {
      return;
    }
    const searchKey = debouncedAtQuery;
    const searchChanged = prevAtSearchKeyRef.current !== searchKey;
    if (searchChanged) {
      prevAtSearchKeyRef.current = searchKey;
      setHighlightIndex(atFlatItems.length > 0 ? 0 : -1);
      return;
    }
    if (!atModeOpenedRef.current) {
      atModeOpenedRef.current = true;
      setHighlightIndex(atFlatItems.length > 0 ? 0 : -1);
      return;
    }
    setHighlightIndex((prev) => {
      if (prev >= 0 && prev < atFlatItems.length) {
        return prev;
      }
      if (prev >= atFlatItems.length && atFlatItems.length > 0) {
        return atFlatItems.length - 1;
      }
      if (prev < 0 && atFlatItems.length > 0) {
        return 0;
      }
      return prev;
    });
  }, [isAtMode, atListLoading, atFlatItems.length, debouncedAtQuery]);

  const { dropdownPos } = useMentionDropdownPosition({
    enabled: isAtMode,
    isDropDown,
    layout: 'container',
    dropdownRef,
    inputBoxContainerRef,
    mentionAnchorRef,
    recalcToken: `${mentionQuery}|${opened ? '1' : '0'}`,
  });

  useKbAtScrollDocLoader({
    enabled: isAtMode,
    listEl,
    observeKey: atDocsObserveKey,
    docsByKbId,
    loadKbDocs,
  });

  // @ 模式下 scroll 关闭
  useEffect(() => {
    if (!isAtMode) return;
    const onScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as HTMLElement)) return;
      closeAtDropdown();
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [isAtMode, closeAtDropdown]);

  // 面板外点击关闭
  useDismissOutside({
    enabled: isAtMode,
    onClose: closeAtDropdown,
    insideSelectors: inputBoxContainerRef
      ? [dropdownRef, inputBoxContainerRef]
      : [dropdownRef],
  });

  // 键盘导航时滚动高亮项到可见区域（不因文档加载扩列表而触发）
  useEffect(() => {
    if (!isAtMode || atFlatItems.length === 0 || highlightIndex < 0) {
      return;
    }
    const list = listRef.current;
    if (!list) {
      return;
    }
    const items = list.querySelectorAll(
      `.${PREFIX}__at-group-header, .${PREFIX}__at-item`,
    );
    const target = items[highlightIndex] as HTMLElement | undefined;
    if (target) {
      target.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex, isAtMode]);

  // ─── @ 模式：选中处理 ───
  const handleAtSelect = useCallback(
    (item: TAtFlatItem) => {
      let added: ISelectedKnowledgeTagData[] = [];
      if (item.type === 'kb') {
        const wholeTag: ISelectedKnowledgeTagData = {
          kbId: item.kb.kbId,
          kbName: item.kb.kbName,
          tenantId: item.kb.tenantId,
        };
        if (selectedItems.some(
          (selected) => selected.kbId === item.kb.kbId && isWholeKbTag(selected),
        )) {
          return;
        }
        const rest = selectedItems.filter((selected) => selected.kbId !== item.kb.kbId);
        added = [wholeTag];
        unstable_batchedUpdates(() => {
          onSelectedChange?.([...rest, wholeTag]);
          onAtModeSelect?.(buildKnowledgeInlineLabel(added, kbDocUnit), added);
          closeAtDropdown();
        });
      } else {
        const tagData: ISelectedKnowledgeTagData = {
          kbId: item.kb.kbId,
          kbName: item.kb.kbName,
          tenantId: resolveTenantId(item.doc.tenantId, item.kb.tenantId),
          docId: item.doc.docId,
          docName: item.doc.docName,
          fileName: item.doc.fileName,
          fileType: item.doc.fileType,
        };
        if (selectedIds.has(getKnowledgeTagKey(tagData))) return;
        added = [tagData];
        unstable_batchedUpdates(() => {
          onSelectedChange?.([...selectedItems, tagData]);
          onAtModeSelect?.(buildKnowledgeInlineLabel(added, kbDocUnit), added);
          closeAtDropdown();
        });
      }
    },
    [selectedItems, onSelectedChange, onAtModeSelect, closeAtDropdown, selectedIds, kbDocUnit],
  );

  // ─── @ 模式：键盘导航 ───
  const handleAtKeyDown = useCallback(
    (e: KeyboardEvent): boolean => {
      if (!isAtMode || atListLoading) return false;
      const maxIdx = Math.max(atFlatItems.length - 1, 0);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          if (atFlatItems.length === 0) return true;
          setHighlightIndex((prev) => (prev < 0 ? 0 : Math.min(prev + 1, maxIdx)));
          return true;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          if (atFlatItems.length === 0) return true;
          setHighlightIndex((prev) => (prev < 0 ? 0 : Math.max(prev - 1, 0)));
          return true;
        case 'Enter':
          e.preventDefault();
          e.stopPropagation();
          if (highlightIndex >= 0 && atFlatItems[highlightIndex]) {
            handleAtSelect(atFlatItems[highlightIndex]);
          }
          return true;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          closeAtDropdown();
          return true;
        default:
          return false;
      }
    },
    [isAtMode, atListLoading, atFlatItems, highlightIndex, handleAtSelect, closeAtDropdown],
  );

  // 暴露键盘处理给外部（通过 ref 或 context，这里用全局事件）
  useEffect(() => {
    if (!isAtMode) return;
    const onKeyDown = (e: KeyboardEvent) => {
      handleAtKeyDown(e);
    };
    // 使用 capture 确保在 textarea 的 onKeyDown 之前拦截
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isAtMode, handleAtKeyDown]);

  const atDropdownPanel = (
    <div
      className={classNames(`${PREFIX}__at-dropdown`, 'dba-nav-menu-overlay')}
      onMouseDown={(e) => e.preventDefault()}
    >
      <MentionDropdownSectionTitle titleId="dba_kb_first_level_title" />
      <div
        ref={setListRef}
        className={`${PREFIX}__at-list`}
      >
        {atListLoading && atFilteredData.length === 0 && <MentionDropdownSkeleton />}
        {!atListLoading && kbError && (
          <div className={`${PREFIX}__at-empty`}>
            {formatMessage({ id: 'common_loading_failed' })}
          </div>
        )}
        {!atListLoading && !kbError && atFilteredData.length === 0 && (
          <div className={`${PREFIX}__at-empty`}>
            {formatMessage({ id: 'dba_kb_mention_empty' })}
          </div>
        )}
        {!kbError && atFilteredData.map(({ kb, docs }) => {
          const allDocsSelected = docs.length > 0 && docs.every(
            (doc) => selectedIds.has(`${kb.kbId}_${doc.docId}`),
          );
          const groupHeaderIndex = atFlatItems.findIndex(
            (item) => item.type === 'kb' && item.kb.kbId === kb.kbId,
          );

          return (
            <div
              key={kb.kbId}
              className={`${PREFIX}__at-group`}
              data-kb-id={String(kb.kbId)}
            >
              {/* 分组头 — 点击选中当前分组下所有文档 */}
              <div
                className={classNames(`${PREFIX}__at-group-header`, {
                  [`${PREFIX}__at-group-header--selected`]: allDocsSelected,
                  [`${PREFIX}__at-group-header--highlight`]: highlightIndex === groupHeaderIndex,
                })}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAtSelect({ type: 'kb', kb, docs } satisfies TAtFlatItem);
                }}
              >
                <span className={`${PREFIX}__at-group-header-line`} />
                <BasicImage src={KB_ICON} alt="" className={`${PREFIX}__at-item-icon`} />
                <span className={`${PREFIX}__at-item-name`}>
                  <HighlightText name={kb.kbName} keyword={filterKeyword} ellipsis overlayClassName="dba-nav-menu-overlay dba-kb-at-item-bubble" />
                </span>
                <span className={`${PREFIX}__at-group-header-divider`} />
                <span className={`${PREFIX}__at-group-header-count`}>
                  {docs.length || kb.docCount || 0} {formatMessage({ id: 'dba_kb_doc_count_unit' })}
                </span>
              </div>
              {/* 文档列表 */}
              {docs.map((doc) => {
                const docItemIndex = atFlatItems.findIndex(
                  (item) => item.type === 'doc' && item.doc.docId === doc.docId,
                );
                const docSelected = selectedIds.has(`${kb.kbId}_${doc.docId}`);

                return (
                  <div
                    key={doc.docId}
                    className={classNames(`${PREFIX}__at-item`, `${PREFIX}__at-doc`, {
                      [`${PREFIX}__at-item--highlight`]: highlightIndex === docItemIndex,
                    })}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAtSelect({ type: 'doc', kb, doc } satisfies TAtFlatItem);
                    }}
                  >
                    <BasicImage src={getChannelIcon(doc.channel, doc.fileType)} alt="" className={`${PREFIX}__at-item-icon`} />
                    <span className={`${PREFIX}__at-item-name`}>
                      <HighlightText name={doc.docName} keyword={filterKeyword} ellipsis overlayClassName="dba-nav-menu-overlay dba-kb-at-item-bubble" />
                    </span>
                    {docSelected && (
                      <span className={`${PREFIX}__at-item-check`}>✓</span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div
      className={classNames(PREFIX, className, {
        [`${PREFIX}--icon-only`]: isCopilot,
      })}
    >
      {/* 触发器按钮始终展示（@ 模式下按钮不消失） */}
      <InputTriggerHoverTip
        enabled={!enableHover}
        content={formatMessage({ id: 'dba_kb_mention_trigger' })}
        suppressVisible={opened}
      >
        <NewRegionSelect
          key={regionMountKey}
          options={knowledgeOptions}
          radioOptions={[]}
          value={regionSelectDisplayValue}
          defaultSelectedRegion={defaultSelectedRegion || undefined}
          onChange={handleRegionSelectDisplayChange}
          onCloseReginSelect={handleRegionSelectClose}
          shortcutOptions={[]}
          locale={locale}
          loading={kbPanelLoading}
          error={kbPanelError}
          searchTagMode="region"
          getCustomRegionSelectAllButton={(ctx) => (
            <span
              className={ctx.classname}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleKbRegionSelectAll();
              }}
            >
              {ctx.defaultText}
            </span>
          )}
          getCustomRegionClearAllButton={(ctx) => (
            <span
              className={ctx.classname}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleKbRegionClearAll();
              }}
            >
              {ctx.defaultText}
            </span>
          )}
          getCustomMarketSelectAllButton={(ctx) => (
            <span
              className={ctx.classname}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleKbMarketSelectAll();
              }}
            >
              {ctx.defaultText}
            </span>
          )}
          getCustomMarketClearAllButton={(ctx) => (
            <span
              className={ctx.classname}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleKbMarketClearAll();
              }}
            >
              {ctx.defaultText}
            </span>
          )}
          firstLevelTitle={formatMessage({ id: 'dba_kb_first_level_title' })}
          resolveSecondLevelTitle={(item) => String(item?.textCN || item?.text || '')}
          renderRightContent={renderKbRightContent}
          enableExactOverflowBubble
          enableSearchTagOverflowBubble
          leftBubblePlacement="top"
          leftBubblePlacementOffset={8}
          rightBubblePlacement="top"
          rightBubblePlacementOffset={8}
          overflowBubbleProps={{
            listRow: {
              overlayClassName: 'dba-nav-menu-overlay dba-kb-popover-list-bubble',
              escapeWithReference: true,
              arrowPointAtCenter: true,
            },
            searchTag: {
              overlayClassName: 'dba-nav-menu-overlay dba-kb-popover-search-tag-bubble',
              escapeWithReference: true,
              closeOnScroll: false,
              dark: true,
              arrowPointAtCenter: true,
              placement: 'top',
              placementOffset: 10,
            },
          }}
          rightListWidth={KB_POPOVER_RIGHT_LIST_WIDTH}
          rightListHeight={KB_POPOVER_RIGHT_LIST_HEIGHT}
          button={(
            <KbButtonTrigger
              triggerRef={triggerRef}
              enableHover={enableHover}
              opened={opened}
              openReason={openReason}
              isCopilot={isCopilot}
              onMouseEnter={handleTriggerMouseEnter}
              onMouseDown={handleTriggerMouseDown}
              onClick={handleTriggerClick}
            />
          )}
          header={null}
          contentHeader={null}
          contentLeftFooter={(item) => (
            <KbActiveRegionDocsLoader
              kbId={item?.value}
              loadKbDocs={loadKbDocs}
              onActiveKbId={handleActivePanelKbId}
            />
          )}
          overlayClassName="dba-kb-popover-overlay dbc-region-select-box dba-nav-menu-overlay"
          boxClassName="dba-kb-popover-box"
          className="dba-kb-popover-trigger"
          appearance="pure"
          open={opened && !isAtMode}
          onOpenChange={handleOpenChange}
          placement={isDropDown ? 'bottom-start' : 'top-start'}
          placementOffset={4}
          flipped={false}
          escapeWithReference={isDropDown}
          updateOnChildrenChange
          closeOnScroll={false}
        />
      </InputTriggerHoverTip>

      {isAtMode && dropdownPos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className={classNames(
            `${PREFIX}__at-overlay`,
            'dba-agent-selector-dropdown-overlay',
            'dba-nav-menu-overlay',
            {
              [`${PREFIX}__at-overlay--up`]: !isDropDown,
              [`${PREFIX}__at-overlay--down`]: isDropDown,
            },
          )}
          style={{
            position: 'fixed',
            ...(dropdownPos.top !== undefined ? { top: dropdownPos.top } : {}),
            ...(dropdownPos.bottom !== undefined ? { bottom: dropdownPos.bottom } : {}),
            left: dropdownPos.left,
            ...(dropdownPos.width !== undefined ? { width: dropdownPos.width } : {}),
            zIndex: 2001,
            boxSizing: 'border-box',
          }}
        >
          {atDropdownPanel}
        </div>,
        document.body,
      )}

      {isButtonMode && (
        <KbPanelSearchInputPortal
          overlayEl={overlayEl}
          value={panelSearchInput}
          onChange={handlePanelSearchChange}
          placeholder={formatMessage({ id: 'search' })}
          mountToken={regionMountKey}
        />
      )}
    </div>
  );
};

export default memo(KnowledgeBaseMention);
